import { create } from 'zustand';
import { apiClient } from '@/services/api';
import type { User, Assignment, UserAchievement } from '@/types';

let fetchStudentsPromise: Promise<void> | null = null;

interface StudentState {
  students: User[];
  hasLoaded: boolean;
  selectedStudent: User | null;
  studentAssignments: Assignment[];
  studentAchievements: Map<string, UserAchievement[]>; // gameId -> achievements
  isLoading: boolean;
  error: string | null;
  fetchStudents: (force?: boolean) => Promise<void>;
  selectStudent: (student: User | null) => void;
  fetchStudentAssignments: (studentId: string) => Promise<void>;
  fetchStudentAchievements: (studentId: string, gameId: string) => Promise<void>;
  clearError: () => void;
}

export const useStudentStore = create<StudentState>((set, get) => ({
  students: [],
  hasLoaded: false,
  selectedStudent: null,
  studentAssignments: [],
  studentAchievements: new Map(),
  isLoading: false,
  error: null,

  fetchStudents: async (force = false) => {
    if (!force && get().hasLoaded) {
      return;
    }

    if (fetchStudentsPromise) {
      return fetchStudentsPromise;
    }

    set({ isLoading: true, error: null });
    fetchStudentsPromise = (async () => {
      try {
        const response = await apiClient.getAllStudents();
        if (response.success) {
          set({ students: response.data, hasLoaded: true, isLoading: false });
        } else {
          throw new Error(response.error || 'Failed to fetch students');
        }
      } catch (error: any) {
        set({ 
          error: error.response?.data?.message || error.message || 'Failed to fetch students', 
          isLoading: false 
        });
      } finally {
        fetchStudentsPromise = null;
      }
    })();

    return fetchStudentsPromise;
  },

  selectStudent: (student: User | null) => {
    set({ selectedStudent: student, studentAssignments: [], studentAchievements: new Map() });
  },

  fetchStudentAssignments: async (studentId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.getStudentGames(studentId);
      if (response.success) {
        set({ studentAssignments: response.data, isLoading: false });
      } else {
        throw new Error(response.error || 'Failed to fetch student assignments');
      }
    } catch (error: any) {
      set({ 
        error: error.response?.data?.message || error.message || 'Failed to fetch student assignments', 
        isLoading: false 
      });
    }
  },

  fetchStudentAchievements: async (studentId: string, gameId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.getUserAchievements(studentId, gameId);
      if (response.success) {
        const achievements = get().studentAchievements;
        achievements.set(gameId, response.data);
        set({ studentAchievements: new Map(achievements), isLoading: false });
      } else {
        throw new Error(response.error || 'Failed to fetch student achievements');
      }
    } catch (error: any) {
      set({ 
        error: error.response?.data?.message || error.message || 'Failed to fetch student achievements', 
        isLoading: false 
      });
    }
  },

  clearError: () => set({ error: null }),
}));

